import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Cookies.css';

const Cookies = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Política de Cookies | Graphyra</title>
        <meta name="description" content="Información sobre el uso de cookies en Graphyra" />
      </Helmet>

      <div className="legal-container">
        <div className="legal-header">
          <h1 className="legal-title">Política de Cookies</h1>
          <p className="legal-update">Última actualización: 3 de enero de 2026</p>
          <p className="legal-intro">
            Esta Política de Cookies explica qué son las cookies, cómo las utilizamos en Graphyra, 
            qué tipos de cookies empleamos, y cómo puedes controlarlas.
          </p>
          <div className="legal-notice">
            <strong>Tu control:</strong> Puedes gestionar tus preferencias de cookies en cualquier momento 
            desde la configuración de tu navegador.
          </div>
        </div>

        <nav className="legal-nav">
          <a href="#que-son">¿Qué son las cookies?</a>
          <a href="#como-usamos">Cómo las usamos</a>
          <a href="#tipos">Tipos de cookies</a>
          <a href="#terceros">Cookies de terceros</a>
          <a href="#gestion">Gestionar cookies</a>
          <a href="#cambios">Cambios</a>
          <a href="#contacto">Contacto</a>
        </nav>

        <div className="legal-content">
          <section id="que-son" className="legal-section">
            <h2>1. ¿Qué son las Cookies?</h2>
            <p>
              Las cookies son pequeños archivos de texto que los sitios web almacenan en tu dispositivo 
              (ordenador, tablet, smartphone) cuando los visitas. Se utilizan ampliamente para hacer que 
              los sitios web funcionen de manera más eficiente y proporcionar información a los propietarios del sitio.
            </p>

            <div className="info-box">
              <h4>📝 Características de las cookies:</h4>
              <ul className="legal-list">
                <li><strong>Tamaño pequeño:</strong> Generalmente menos de 4KB de datos.</li>
                <li><strong>Vida útil variable:</strong> Pueden ser temporales (sesión) o persistentes.</li>
                <li><strong>Específicas del dominio:</strong> Solo el sitio que las crea puede leerlas.</li>
                <li><strong>No son ejecutables:</strong> No pueden ejecutar código ni instalar virus.</li>
              </ul>
            </div>

            <h3>1.1. Cookies de Sesión vs Persistentes</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Cookies de Sesión</strong>
                <p>
                  Se eliminan automáticamente cuando cierras tu navegador. Se utilizan para recordar tus 
                  acciones durante una visita específica al sitio.
                </p>
              </div>
              <div className="definition-item">
                <strong>Cookies Persistentes</strong>
                <p>
                  Permanecen en tu dispositivo durante un período específico (días, meses o años). 
                  Se utilizan para recordar tus preferencias entre visitas.
                </p>
              </div>
            </div>

            <h3>1.2. Cookies Propias vs de Terceros</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Cookies Propias (First-party)</strong>
                <p>
                  Establecidas directamente por Graphyra. Solo nosotros podemos leer la información que contienen.
                </p>
              </div>
              <div className="definition-item">
                <strong>Cookies de Terceros (Third-party)</strong>
                <p>
                  Establecidas por servicios externos integrados en nuestro sitio (como Google reCAPTCHA). 
                  Estos terceros pueden rastrear tu actividad en múltiples sitios web.
                </p>
              </div>
            </div>
          </section>

          <section id="como-usamos" className="legal-section">
            <h2>2. Cómo Utilizamos las Cookies</h2>
            <p>
              En Graphyra utilizamos cookies para diversos propósitos que mejoran tu experiencia de usuario 
              y ayudan a mantener la seguridad y funcionalidad de la plataforma.
            </p>

            <h3>2.1. Propósitos Principales</h3>
            <div className="info-box">
              <h4>✅ Usos legítimos de cookies:</h4>
              <ul className="legal-list">
                <li><strong>Autenticación:</strong> Mantener tu sesión activa después de iniciar sesión.</li>
                <li><strong>Seguridad:</strong> Detectar y prevenir actividades fraudulentas o abusivas.</li>
                <li><strong>Preferencias:</strong> Recordar tus configuraciones (idioma, tema, etc.).</li>
                <li><strong>Funcionalidad:</strong> Habilitar características como el carrito o favoritos.</li>
                <li><strong>Rendimiento:</strong> Analizar cómo usas el sitio para mejorarlo.</li>
                <li><strong>Personalización:</strong> Adaptar contenido a tus intereses y comportamiento.</li>
              </ul>
            </div>

            <h3>2.2. Base Legal</h3>
            <p>
              Utilizamos cookies esenciales basándonos en nuestro interés legítimo de operar el sitio. 
              Para cookies no esenciales, solicitamos tu consentimiento cuando sea requerido por la legislación aplicable.
            </p>
          </section>

          <section id="tipos" className="legal-section">
            <h2>3. Tipos de Cookies que Utilizamos</h2>
            
            <div className="cookie-category">
              <h3>🔐 Cookies Estrictamente Necesarias</h3>
              <p>
                <strong>Propósito:</strong> Esenciales para el funcionamiento básico del sitio. 
                No pueden ser desactivadas sin afectar la funcionalidad.
              </p>
              <div className="cookie-table">
                <div className="cookie-row">
                  <div className="cookie-name"><strong>auth_token</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 7 días</p>
                    <p><strong>Función:</strong> Mantiene tu sesión de usuario activa.</p>
                  </div>
                </div>
                <div className="cookie-row">
                  <div className="cookie-name"><strong>session_id</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> Sesión</p>
                    <p><strong>Función:</strong> Identifica tu sesión de navegación única.</p>
                  </div>
                </div>
                <div className="cookie-row">
                  <div className="cookie-name"><strong>csrf_token</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> Sesión</p>
                    <p><strong>Función:</strong> Protección contra ataques de falsificación de peticiones.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="cookie-category">
              <h3>🛡️ Cookies de Seguridad</h3>
              <p>
                <strong>Propósito:</strong> Detectar y prevenir abuso, fraude y acceso no autorizado.
              </p>
              <div className="cookie-table">
                <div className="cookie-row">
                  <div className="cookie-name"><strong>security_check</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 24 horas</p>
                    <p><strong>Función:</strong> Verifica patrones de uso para detectar bots.</p>
                  </div>
                </div>
                <div className="cookie-row">
                  <div className="cookie-name"><strong>rate_limit</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 1 hora</p>
                    <p><strong>Función:</strong> Previene abuso mediante limitación de tasa.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="cookie-category">
              <h3>⚙️ Cookies de Preferencias</h3>
              <p>
                <strong>Propósito:</strong> Recordar tus configuraciones y preferencias personales.
              </p>
              <div className="cookie-table">
                <div className="cookie-row">
                  <div className="cookie-name"><strong>user_preferences</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 1 año</p>
                    <p><strong>Función:</strong> Guarda idioma, tema y otras configuraciones UI.</p>
                  </div>
                </div>
                <div className="cookie-row">
                  <div className="cookie-name"><strong>cookie_consent</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 1 año</p>
                    <p><strong>Función:</strong> Registra tus preferencias de cookies.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="cookie-category">
              <h3>📊 Cookies de Rendimiento y Analítica</h3>
              <p>
                <strong>Propósito:</strong> Recopilar información sobre cómo utilizas el sitio para mejorarlo.
              </p>
              <div className="info-box">
                <p>
                  <strong>Nota:</strong> Estas cookies recopilan información de forma agregada y anónima. 
                  No te identifican personalmente.
                </p>
              </div>
              <div className="cookie-table">
                <div className="cookie-row">
                  <div className="cookie-name"><strong>_analytics</strong></div>
                  <div className="cookie-details">
                    <p><strong>Duración:</strong> 2 años</p>
                    <p><strong>Función:</strong> Análisis de uso del sitio y métricas de rendimiento.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="terceros" className="legal-section">
            <h2>4. Cookies de Terceros</h2>
            <p>
              Algunos servicios externos que integramos en Graphyra pueden establecer sus propias cookies. 
              No controlamos estas cookies y te recomendamos revisar las políticas de privacidad de estos servicios.
            </p>

            <h3>4.1. Google reCAPTCHA</h3>
            <div className="info-box">
              <p><strong>Proveedor:</strong> Google LLC</p>
              <p><strong>Propósito:</strong> Protección contra bots y spam</p>
              <p><strong>Cookies utilizadas:</strong> _GRECAPTCHA, NID</p>
              <p><strong>Política de privacidad:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>
              <p><strong>Tipo:</strong> Cookies de seguridad y funcionalidad</p>
            </div>

            <h3>4.2. Otros Servicios</h3>
            <p>
              Dependiendo de las funcionalidades que utilices, podrían activarse cookies de otros servicios 
              (reproductores de video, mapas, widgets de redes sociales). Te notificaremos sobre estos servicios 
              cuando corresponda.
            </p>
          </section>

          <section id="gestion" className="legal-section">
            <h2>5. Cómo Gestionar y Controlar las Cookies</h2>
            
            <h3>5.1. Configuración del Navegador</h3>
            <p>
              La mayoría de los navegadores aceptan cookies automáticamente, pero puedes modificar la configuración 
              para rechazarlas o ser notificado cuando se envíen.
            </p>

            <div className="info-box">
              <h4>🌐 Instrucciones por navegador:</h4>
              <ul className="legal-list">
                <li><strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies y otros datos de sitios</li>
                <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad → Cookies y datos del sitio</li>
                <li><strong>Safari:</strong> Preferencias → Privacidad → Cookies y datos de sitios web</li>
                <li><strong>Microsoft Edge:</strong> Configuración → Cookies y permisos de sitio → Cookies y datos almacenados</li>
              </ul>
            </div>

            <h3>5.2. Consecuencias de Deshabilitar Cookies</h3>
            <div className="warning-box">
              <h4>⚠️ Importante:</h4>
              <p>
                Si deshabilitas cookies, algunas funcionalidades del sitio pueden no funcionar correctamente:
              </p>
              <ul className="legal-list">
                <li>No podrás mantener tu sesión iniciada entre páginas</li>
                <li>Las preferencias de usuario no se guardarán</li>
                <li>Algunas características interactivas pueden no funcionar</li>
                <li>La experiencia de usuario puede verse degradada</li>
              </ul>
            </div>

            <h3>5.3. Herramientas de Exclusión</h3>
            <p>Puedes optar por no participar en ciertos tipos de seguimiento mediante:</p>
            <ul className="legal-list">
              <li><strong>Do Not Track (DNT):</strong> Función del navegador que solicita a los sitios no rastrear tu actividad</li>
              <li><strong>Extensiones de bloqueo:</strong> Como Privacy Badger, uBlock Origin, Ghostery</li>
              <li><strong>Configuración de publicidad:</strong> Opciones de opt-out en plataformas de publicidad</li>
            </ul>

            <h3>5.4. Borrar Cookies Existentes</h3>
            <p>
              Puedes eliminar cookies que ya están en tu dispositivo a través de la configuración de tu navegador. 
              Ten en cuenta que esto puede cerrar sesiones activas y eliminar preferencias guardadas.
            </p>
          </section>

          <section id="cambios" className="legal-section">
            <h2>6. Cambios a Esta Política</h2>
            <p>
              Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en las cookies que 
              utilizamos o por razones operativas, legales o regulatorias.
            </p>
            <p>
              La versión actual siempre estará disponible en esta página, con la fecha de última actualización 
              indicada al inicio del documento.
            </p>
            <p className="legal-note">
              Te recomendamos revisar esta política periódicamente para mantenerte informado sobre cómo utilizamos las cookies.
            </p>
          </section>

          <section id="contacto" className="legal-section">
            <h2>7. Más Información y Contacto</h2>
            <p>
              Si tienes preguntas sobre nuestra Política de Cookies o sobre cómo utilizamos cookies en general, 
              no dudes en contactarnos:
            </p>
            <div className="contact-box">
              <p><strong>📧 Correo de contacto:</strong> contact.graphyra@gmail.com</p>
              <p><strong>📧 Correo de soporte:</strong> support.graphyra@gmail.com</p>
              <p><strong>📞 Teléfono:</strong> +57 3115491733</p>
              <p><strong>🌐 Formulario:</strong> <Link to="/contact">Página de contacto</Link></p>
            </div>

            <div className="info-box">
              <h4>📚 Recursos adicionales:</h4>
              <ul className="legal-list">
                <li><strong>Sobre cookies:</strong> <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">AllAboutCookies.org</a></li>
                <li><strong>Gestión de privacidad:</strong> <a href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">YourOnlineChoices.com</a></li>
                <li><strong>Protección de datos:</strong> Consulta nuestra <Link to="/privacy">Política de Privacidad</Link></li>
              </ul>
            </div>
          </section>

          <div className="legal-footer">
            <p>
              Esta Política de Cookies complementa nuestra Política de Privacidad y Términos y Condiciones.
            </p>
            <div className="legal-links">
              <Link to="/privacy">Política de Privacidad</Link>
              <span className="separator">•</span>
              <Link to="/terms">Términos y Condiciones</Link>
              <span className="separator">•</span>
              <Link to="/">Volver al inicio</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cookies;
